import classNames from "classnames";
import React, { FC, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import { CHAT_TOKEN_EXPIRE_TIME_TO_CHECK } from "~/constants";
import {
  SelectedWindows,
  useChatAuthUserData,
  useChatLeftMenuSettings,
  useChatSelectedWindow,
  useChatSettings,
  useSelectedChat,
} from "~/ui/context";
import { useScreenMd } from "~/utils";

import { LoadingIcon } from "../icons";
import { ChatConversation } from "./chatConversation/ChatConversation";
import { ChatHeader } from "./chatHeader/ChatHeader";
import { ChatLeftMenu } from "./chatLeftMenu/ChatLeftMenu";
import { ChatNotifications } from "./chatNotifications/ChatNotifications";
import { CreateChat } from "./createChat/CreateChat";
import { CreateSystemChat } from "./createSystemChat/CreateSystemChat";

interface ChatProps {
  elapsedTime: number;
  onFinish: () => void;
}

export const Chat: FC<ChatProps> = ({ elapsedTime, onFinish }) => {
  const { isMenuOpened } = useChatLeftMenuSettings();
  const { isChatOpen, setIsChatOpen } = useChatSettings();

  const { selectedChat } = useSelectedChat();
  const { selectedWindow } = useChatSelectedWindow();
  const isDesktop = useScreenMd();
  const { handleShouldRefresh } = useChatAuthUserData();
  const { isChatAuthUserLoading } = useChatAuthUserData();

  const onClose = () => {
    setIsChatOpen(false);
  };

  useEffect(() => {
    if (elapsedTime > CHAT_TOKEN_EXPIRE_TIME_TO_CHECK) {
      handleShouldRefresh(true, onFinish);
    }
  }, [elapsedTime, onFinish, handleShouldRefresh]);

  if (!isChatOpen) return null;

  if (isChatAuthUserLoading) {
    return (
      <div className="bg-primary-light fixed border justify-center items-center flex w-100 h-full z-index-1000">
        <LoadingIcon />
      </div>
    );
  }

  return (
    <div className="bg-dark fixed border flex w-100 h-full z-index-1000">
      <ChatLeftMenu />
      <div
        className={classNames(
          "flex flex-col h-full bg-dark",
          !isMenuOpened && !isDesktop ? "flex-col" : "flex-row justify-between",
          isMenuOpened ? "w-70" : !isDesktop ? "w-100" : "w-95"
        )}
      >
        <ChatHeader onClose={onClose} />

        {selectedWindow === SelectedWindows.None && (
          <div className="w-100 h-full flex items-center justify-center">
            <h6 className="text-center">
              <FormattedMessage
                defaultMessage="Select a conversation"
                id="328WqF"
              />
            </h6>
          </div>
        )}
        {selectedWindow === SelectedWindows.Conversation && selectedChat && (
          <ChatConversation chatId={selectedChat} />
        )}
        {selectedWindow === SelectedWindows.CreateChat && <CreateChat />}
        {selectedWindow === SelectedWindows.Notifications && (
          <ChatNotifications />
        )}
        {selectedWindow === SelectedWindows.SystemChat && <CreateSystemChat />}
      </div>
    </div>
  );
};
